import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  ArrowLeft, Search, BookMarked, Calendar, Trash2, ArrowUpRight, ShoppingCart, Check, Edit2, Play, X
} from 'lucide-react';
import { Haptics } from '../../utils/haptics';

export interface HeldBill {
  id: string;
  label: string;
  items: any[];
  time: Date;
}

interface HoldManagerProps {
  heldBills: HeldBill[];
  onBack: () => void;
  onRestore: (id: string) => void;
  onDelete: (id: string) => void;
  onUpdateLabel: (id: string, newLabel: string) => void;
  formatRs: (n: number) => string;
}

export const HoldManager: React.FC<HoldManagerProps> = ({
  heldBills,
  onBack,
  onRestore,
  onDelete,
  onUpdateLabel,
  formatRs,
}) => {
  const [query, setQuery] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editVal, setEditVal] = useState('');

  const filtered = useMemo(() => {
    return heldBills.filter(b => {
      const labelMatch = b.label.toLowerCase().includes(query.toLowerCase());
      const itemMatch = b.items.some(i => i.product.name.toLowerCase().includes(query.toLowerCase()));
      return labelMatch || itemMatch;
    });
  }, [heldBills, query]);

  return (
    <div className="fixed inset-0 z-[60] bg-gradient-to-br from-slate-50 via-white to-sky-50 flex flex-col">
      {/* Header */}
      <div className="shrink-0 flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-white/80 backdrop-blur-md shadow-sm">
        <div className="flex items-center gap-2.5">
          <button
            onClick={onBack}
            className="w-10 h-10 rounded-xl bg-slate-50 hover:bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-600 transition-colors active:scale-95"
            aria-label="Back"
          >
            <ArrowLeft size={16} />
          </button>
          <div>
            <h2 className="text-[15px] font-black text-slate-800 leading-tight flex items-center gap-2">
              <BookMarked size={16} className="text-amber-500" /> Held Bills Dashboard
            </h2>
            <p className="text-[10px] text-slate-500">Manage, rename, and retrieve temporarily saved customer baskets</p>
          </div>
        </div>
        <div className="text-[10px] font-mono font-bold text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1">
          {heldBills.length} Bills Suspended
        </div>
      </div>

      {/* Search Toolbar */}
      <div className="shrink-0 p-4 border-b border-slate-100 bg-white/50 flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search suspended bills by label or item name…"
            className="w-full pl-10 pr-3 py-2.5 rounded-2xl bg-white border border-slate-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/10 outline-none text-[12px] text-slate-800 font-medium shadow-sm transition-all"
          />
        </div>
      </div>

      {/* Main Grid View */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center space-y-3">
            <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
              <BookMarked size={22} className="opacity-40" />
            </div>
            <div>
              <p className="text-[13px] font-bold text-slate-700">No suspended bills found</p>
              <p className="text-[10px] text-slate-400">Search query returned no results, or there are no bills currently on hold</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            <AnimatePresence>
              {filtered.map((bill) => {
                const billTotal = bill.items.reduce((sum, i) => {
                  const unitPrice = parseFloat(i.product.price);
                  const itemDiscount = parseFloat(i.itemDiscount) || 0;
                  return sum + (unitPrice - itemDiscount) * i.qty;
                }, 0);

                return (
                  <motion.div
                    key={bill.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="bg-white border border-slate-200/80 rounded-2xl shadow-sm p-4 hover:shadow-md transition-all flex flex-col justify-between"
                  >
                    <div className="space-y-3.5">
                      {/* Bill Meta */}
                      <div className="flex items-start justify-between gap-1">
                        <div className="flex-1 min-w-0">
                          {editingId === bill.id ? (
                            <div className="flex items-center gap-1">
                              <input
                                type="text"
                                value={editVal}
                                onChange={(e) => setEditVal(e.target.value)}
                                className="bg-slate-50 border border-amber-300 rounded px-2 py-0.5 text-[11px] font-bold text-slate-800 outline-none w-full"
                                autoFocus
                              />
                              <button
                                onClick={() => {
                                  if (editVal.trim()) {
                                    onUpdateLabel(bill.id, editVal.trim());
                                    setEditingId(null);
                                    Haptics.fire('success');
                                  }
                                }}
                                className="w-6 h-6 rounded bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-600"
                              >
                                <Check size={11} />
                              </button>
                              <button
                                onClick={() => setEditingId(null)}
                                className="w-6 h-6 rounded bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-400"
                              >
                                <X size={11} />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5 group">
                              <h3 className="text-[12px] font-black text-slate-800 truncate leading-tight">{bill.label}</h3>
                              <button
                                onClick={() => {
                                  setEditingId(bill.id);
                                  setEditVal(bill.label);
                                }}
                                className="opacity-0 group-hover:opacity-100 p-0.5 rounded text-slate-400 hover:text-slate-600 transition-opacity"
                              >
                                <Edit2 size={10} />
                              </button>
                            </div>
                          )}
                          <div className="flex items-center gap-1.5 text-[9px] text-slate-400 mt-1">
                            <Calendar size={10} />
                            <span>{new Date(bill.time).toLocaleString('en-LK')}</span>
                          </div>
                        </div>
                        <span className="text-[12px] font-black text-slate-800 font-mono shrink-0">
                          {formatRs(billTotal)}
                        </span>
                      </div>

                      {/* Items List */}
                      <div className="bg-slate-50 rounded-xl p-2.5 max-h-36 overflow-y-auto border border-slate-100 space-y-1.5">
                        {bill.items.map((i, idx) => (
                          <div key={idx} className="flex justify-between items-center text-[10px] font-medium text-slate-600">
                            <span className="truncate flex-1 pr-2">{i.product.emoji || '📦'} {i.product.name}</span>
                            <span className="font-mono text-slate-400 text-[9px] shrink-0">x{i.qty}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 border-t border-slate-100 pt-3 mt-3.5">
                      <button
                        onClick={() => onDelete(bill.id)}
                        className="p-2 rounded-xl border border-rose-200 hover:bg-rose-50 text-rose-500 transition-colors active:scale-95"
                        title="Delete bill"
                      >
                        <Trash2 size={13} />
                      </button>
                      <button
                        onClick={() => onRestore(bill.id)}
                        className="flex-1 flex items-center justify-center gap-1 px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-black text-[10px] uppercase tracking-wider shadow-md shadow-emerald-500/10 active:scale-95 transition-all"
                      >
                        <ShoppingCart size={11} /> Retrieve Bill
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
};
